import { useEffect, useMemo, useState } from "react";
import { TrendingUp, Package, Calendar, Filter, Globe, Undo2, X, AlertTriangle } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { adminListPayments, adminListBundles, adminRefundOrder } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

function fmt(cents) {
  return `€${(cents / 100).toFixed(2).replace(".", ",")}`;
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function rangeFor(preset) {
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  let from = null;
  let to = isoDate(today);
  if (preset === "month") {
    from = isoDate(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)));
  } else if (preset === "quarter") {
    const q = Math.floor(today.getUTCMonth() / 3);
    from = isoDate(new Date(Date.UTC(today.getUTCFullYear(), q * 3, 1)));
  } else if (preset === "year") {
    from = isoDate(new Date(Date.UTC(today.getUTCFullYear(), 0, 1)));
  } else if (preset === "all") {
    from = null;
    to = null;
  }
  return { from, to };
}

const COUNTRIES = [
  { code: "", label: "Alle landen" },
  { code: "BE", label: "België" },
  { code: "NL", label: "Nederland" },
  { code: "LU", label: "Luxemburg" },
  { code: "FR", label: "Frankrijk" },
  { code: "DE", label: "Duitsland" },
];

export default function AdminRevenue() {
  const { toast } = useToast();
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState(rangeFor("month").from || "");
  const [to, setTo] = useState(rangeFor("month").to || "");
  const [country, setCountry] = useState("");

  const [payments, setPayments] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [total, setTotal] = useState(0);
  const [refundedTotal, setRefundedTotal] = useState(0);
  const [byKind, setByKind] = useState({});
  const [loading, setLoading] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);  // payment object or null
  const [refundReason, setRefundReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundNotify, setRefundNotify] = useState(true);
  const [refundLoading, setRefundLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (country) params.country = country;
      const p = await adminListPayments(params);
      setPayments(p.payments || []);
      setTotal(p.total_cents || 0);
      setByKind(p.by_kind_cents || {});
      const b = await adminListBundles();
      setBundles(b.bundles || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const params = {};
        if (from) params.date_from = from;
        if (to) params.date_to = to;
        if (country) params.country = country;
        const p = await adminListPayments(params);
        if (cancelled) return;
        setPayments(p.payments || []);
        setTotal(p.total_cents || 0);
        setRefundedTotal(p.refunded_cents || 0);
        setByKind(p.by_kind_cents || {});
        const b = await adminListBundles();
        if (!cancelled) setBundles(b.bundles || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [from, to, country]);

  async function reload() {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    if (country) params.country = country;
    const p = await adminListPayments(params);
    setPayments(p.payments || []);
    setTotal(p.total_cents || 0);
    setRefundedTotal(p.refunded_cents || 0);
    setByKind(p.by_kind_cents || {});
  }

  function openRefund(p) {
    setRefundTarget(p);
    const remaining = (p.amount_cents || 0) - (p.refunded_cents || 0);
    setRefundAmount((remaining / 100).toFixed(2).replace(".", ","));
    setRefundReason("");
    setRefundNotify(true);
  }

  async function confirmRefund() {
    if (!refundTarget) return;
    const cents = Math.round(parseFloat(refundAmount.replace(",", ".")) * 100);
    if (!cents || cents <= 0) {
      toast({ title: "Ongeldig bedrag", variant: "destructive" });
      return;
    }
    if (!refundReason.trim()) {
      toast({ title: "Vul een reden in", variant: "destructive" });
      return;
    }
    setRefundLoading(true);
    try {
      const res = await adminRefundOrder(refundTarget.order_id, {
        reason: refundReason.trim(),
        amount_cents: cents,
        notify_customer: refundNotify,
      });
      toast({
        title: "Refund verwerkt",
        description: res.msp_status === "processed"
          ? `€${(cents / 100).toFixed(2)} terugbetaald via MultiSafepay`
          : `€${(cents / 100).toFixed(2)} geregistreerd — MSP vereist handmatige actie (${res.msp_error || "onbekend"})`,
      });
      setRefundTarget(null);
      await reload();
    } catch (err) {
      toast({
        title: "Refund mislukt",
        description: err?.response?.data?.detail || err.message,
        variant: "destructive",
      });
    } finally {
      setRefundLoading(false);
    }
  }

  function applyPreset(p) {
    setPreset(p);
    const r = rangeFor(p);
    setFrom(r.from || "");
    setTo(r.to || "");
  }

  const activeBundles = bundles.filter((b) => b.status === "active");
  const pendingBundles = bundles.filter((b) => b.status === "pending");
  const avg = useMemo(() => (payments.length ? Math.round(total / payments.length) : 0), [payments, total]);

  return (
    <AdminLayout>
      <div data-testid="admin-revenue-page">
        <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Omzet</h1>
        <p className="text-ink-soft text-sm mt-1">Filter per periode en land. Bedragen tonen enkel &ldquo;completed&rdquo; betalingen.</p>

        {/* Filters */}
        <section className="mt-6 rounded-2xl bg-white border border-border p-5" data-testid="revenue-filters">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-ink-soft" />
            <span className="text-xs uppercase tracking-wider font-semibold text-ink-soft">Periode</span>
            {[
              ["month", "Deze maand"],
              ["quarter", "Dit kwartaal"],
              ["year", "Dit jaar"],
              ["all", "Alles"],
              ["custom", "Custom"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => applyPreset(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${preset === k ? "bg-[#002FA7] text-white" : "bg-slate-100 hover:bg-slate-200 text-ink"}`}
                data-testid={`preset-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs"><Calendar className="h-3 w-3 inline mr-1" /> Vanaf</Label>
              <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} data-testid="filter-from" />
            </div>
            <div>
              <Label className="text-xs"><Calendar className="h-3 w-3 inline mr-1" /> Tot</Label>
              <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} data-testid="filter-to" />
            </div>
            <div>
              <Label className="text-xs"><Globe className="h-3 w-3 inline mr-1" /> Land</Label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="filter-country"
              >
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={load} disabled={loading} className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-60" data-testid="apply-filters">
                {loading ? "Laden…" : "Toepassen"}
              </button>
            </div>
          </div>
        </section>

        {/* KPI cards */}
        <div className="grid sm:grid-cols-4 gap-4 mt-6">
          <div className="rounded-2xl bg-white border border-border p-6" data-testid="kpi-total">
            <div className="text-xs uppercase tracking-wider text-ink-soft">Totale omzet</div>
            <div className="font-display text-3xl font-bold text-ink mt-2">{fmt(total)}</div>
            <div className="text-xs text-ink-soft mt-1">{payments.length} betalingen</div>
          </div>
          <div className="rounded-2xl bg-white border border-border p-6" data-testid="kpi-checks">
            <div className="text-xs uppercase tracking-wider text-ink-soft">B2C checks</div>
            <div className="font-display text-3xl font-bold text-ink mt-2">{fmt(byKind.check || 0)}</div>
          </div>
          <div className="rounded-2xl bg-white border border-border p-6" data-testid="kpi-bundles">
            <div className="text-xs uppercase tracking-wider text-ink-soft">Bundles</div>
            <div className="font-display text-3xl font-bold text-ink mt-2">{fmt(byKind.bundle || 0)}</div>
            <div className="text-xs text-ink-soft mt-1">{activeBundles.length} actief · {pendingBundles.length} pending</div>
          </div>
          <div className="rounded-2xl bg-white border border-border p-6" data-testid="kpi-avg">
            <div className="text-xs uppercase tracking-wider text-ink-soft">Terugbetaald</div>
            <div className="font-display text-3xl font-bold text-red-600 mt-2">{fmt(refundedTotal)}</div>
            <div className="text-xs text-ink-soft mt-1">Gemiddeld: {fmt(avg)}</div>
          </div>
        </div>

        <h2 className="font-display text-xl font-semibold text-ink mt-10 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-emerald-600" /> Betalingen
        </h2>
        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-ink-soft text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Order ID</th>
                <th className="px-4 py-3 text-left">Bedrag</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Land</th>
                <th className="px-4 py-3 text-left">Datum</th>
                <th className="px-4 py-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-soft">Geen betalingen voor deze filter.</td></tr>
              ) : (
                payments.map((p) => {
                  const refundedC = p.refunded_cents || 0;
                  const remaining = (p.amount_cents || 0) - refundedC;
                  const isRefunded = p.status === "refunded";
                  const isPartial = p.status === "partially_refunded";
                  const canRefund = p.status === "completed" || isPartial;
                  return (
                    <tr key={p.order_id} className={`border-t border-border ${isRefunded ? "bg-red-50" : ""}`} data-testid={`payment-${p.order_id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{p.order_id}</td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${isRefunded ? "line-through text-red-700" : "text-ink"}`}>{fmt(p.amount_cents || 0)}</span>
                        {refundedC > 0 && <div className="text-[10px] text-red-600">−{fmt(refundedC)} refund</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold ${
                          p.status === "completed" ? "text-emerald-600" :
                          isRefunded ? "text-red-600" :
                          isPartial ? "text-amber-600" : "text-amber-600"
                        }`}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{p.country || "—"}</td>
                      <td className="px-4 py-3 text-ink-soft">{p.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-3 text-right">
                        {canRefund && remaining > 0 ? (
                          <button
                            onClick={() => openRefund(p)}
                            className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-semibold"
                            data-testid={`refund-btn-${p.order_id}`}
                            aria-label={`Refund ${p.order_id}`}
                          >
                            <Undo2 className="h-3.5 w-3.5" /> Refund
                          </button>
                        ) : (
                          <span className="text-ink-soft text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <h2 className="font-display text-xl font-semibold text-ink mt-10 mb-4 flex items-center gap-2">
          <Package className="h-5 w-5 text-[#002FA7]" /> Bundle-aankopen
        </h2>
        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-ink-soft text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">E-mail</th>
                <th className="px-4 py-3 text-left">Credits</th>
                <th className="px-4 py-3 text-left">Bedrag</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Datum</th>
              </tr>
            </thead>
            <tbody>
              {bundles.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-ink-soft">Geen bundles.</td></tr>
              ) : (
                bundles.map((b) => (
                  <tr key={b.id} className="border-t border-border">
                    <td className="px-4 py-3 text-ink-soft">{b.email}</td>
                    <td className="px-4 py-3 font-semibold text-ink">{b.credits_remaining}/{b.credits_total}</td>
                    <td className="px-4 py-3">{fmt(b.amount_cents || 0)}</td>
                    <td className="px-4 py-3 text-xs">{b.status}</td>
                    <td className="px-4 py-3 text-ink-soft">{b.created_at?.slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Refund modal */}
        {refundTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="refund-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-display text-xl font-bold text-ink flex items-center gap-2">
                    <Undo2 className="h-5 w-5 text-red-600" /> Refund bestelling
                  </h3>
                  <p className="text-xs text-ink-soft mt-1 font-mono">{refundTarget.order_id}</p>
                </div>
                <button onClick={() => setRefundTarget(null)} className="text-ink-soft hover:text-ink" aria-label="Sluiten">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2 text-xs text-amber-900">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    Refund wordt aangeroepen op MultiSafepay. Ongebruikte credits worden geïnvalideerd. De klant krijgt automatisch een bevestigingsmail. Deze actie verschijnt als negatieve regel in de Octopus CSV.
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Bedrag (max €{((refundTarget.amount_cents || 0) - (refundTarget.refunded_cents || 0)) / 100})</Label>
                  <Input
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder="0,00"
                    data-testid="refund-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Reden (verplicht — zichtbaar in mail en boekhouding)</Label>
                  <textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    rows={3}
                    className="w-full p-3 border border-input rounded-md text-sm"
                    placeholder="bv. Onjuiste wetgeving in rapport — klantgoodwill"
                    data-testid="refund-reason-input"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-ink-soft cursor-pointer">
                  <input
                    type="checkbox"
                    checked={refundNotify}
                    onChange={(e) => setRefundNotify(e.target.checked)}
                    data-testid="refund-notify-checkbox"
                  />
                  Klant per e-mail op de hoogte stellen
                </label>
              </div>

              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => setRefundTarget(null)}
                  className="flex-1 px-4 py-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-ink text-sm font-semibold"
                  data-testid="refund-cancel-btn"
                >
                  Annuleer
                </button>
                <button
                  onClick={confirmRefund}
                  disabled={refundLoading || !refundReason.trim()}
                  className="flex-1 px-4 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60"
                  data-testid="refund-confirm-btn"
                >
                  {refundLoading ? "Bezig…" : "Bevestig refund"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
